import React, { useState, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTickets } from '../../../contexts/TicketContext';
import { usePayments } from '../../../contexts/PaymentContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ProviderList from './ProviderList';
import ProviderDetails from './ProviderDetails';
import ProviderCreateDialog from './ProviderCreateDialog';
import ApprovalsList from './ApprovalsList';
import InvitesList from './InvitesList';
import { Users, Briefcase, Calendar, UserPlus, UserCheck, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AdminProviders = () => {
    const {
        allUsers,
        adminFullUpdateUser,
        deleteUser,
        startViewAsUser,
        refreshAllUsers,
        preRegisteredStaff,
        staffRegistrations,
        approveStaffRegistration,
        removeStaffPreRegistration
    } = useAuth();
    const { tickets } = useTickets();
    const { payments, updateContract } = usePayments();
    const navigate = useNavigate();

    const [selectedProvider, setSelectedProvider] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleTimeString('pt-BR'));
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    // Renewal State
    const [renewalOpen, setRenewalOpen] = useState(false);
    const [renewalUser, setRenewalUser] = useState(null);
    const [renewalData, setRenewalData] = useState({
        value: '',
        duration: '',
        unit: 'months',
        role: '',
        description: ''
    });

    const getRoleLabel = (role) => {
        return role ? role.replace(/_/g, ' ').toUpperCase() : '';
    };

    const handleRenewalSubmit = async () => {
        if (!renewalUser || !adminFullUpdateUser) return;

        try {
            const updatedStaffDetails = {
                ...renewalUser.staffDetails,
                contractData: {
                    ...renewalUser.staffDetails.contractData,
                    renewalPending: true,
                    status: 'renewing',
                    renewalData: {
                        contractValue: renewalData.value,
                        contractDuration: renewalData.duration,
                        contractUnit: renewalData.unit,
                        jobFunction: getRoleLabel(renewalData.role),
                        serviceDescription: renewalData.serviceDescription,
                        newRole: renewalData.role
                    }
                }
            };

            const result = await adminFullUpdateUser(renewalUser.discordId, {
                staffDetails: updatedStaffDetails
            });

            if (result.success) {
                toast.success(`Proposta de renovação enviada para ${renewalUser.name}`);
                setRenewalOpen(false);
                setRenewalUser(null);
            } else {
                toast.error(result.error || 'Erro ao enviar renovação');
            }
        } catch (error) {
            console.error('Renewal error:', error);
            toast.error('Erro interno ao renovar');
        }
    };

    const openRenewalDialog = (provider) => {
        setRenewalUser(provider);
        setRenewalData({
            value: provider.staffDetails?.contractData?.monthlyValue || provider.staffDetails?.contractData?.contractValue || '',
            duration: provider.staffDetails?.contractData?.contractDuration || provider.staffDetails?.contractData?.contractTerm?.split(' ')[0] || '',
            unit: provider.staffDetails?.contractData?.contractUnit || 'months',
            role: provider.accessLevel,
            description: provider.staffDetails?.contractData?.serviceDescription || ''
        });
        setRenewalOpen(true);
    };

    // Filter and Aggregate Data
    const providers = useMemo(() => {
        if (!allUsers) return [];

        // Updated Filter: Include suspended users
        const staffMembers = allUsers.filter(u => {
            if (!u) return false;
            const hasStaffDetails = !!u.staffDetails;
            const isNotUserRole = u.accessLevel && u.accessLevel !== 'user';
            const isSuspended = u.staffDetails?.isSuspended === true;

            return hasStaffDetails || isNotUserRole || isSuspended;
        });

        return staffMembers.map(user => {
            // Ticket Stats
            const userTickets = tickets.filter(t => t.assignedTo === user.discordId);
            const resolvedTickets = userTickets.filter(t => t.status === 'resolvido' || t.status === 'fechado').length;
            const pendingTickets = userTickets.filter(t => t.status !== 'resolvido' && t.status !== 'fechado').length;

            // Contract Stats
            let daysRemaining = 0;
            const contract = user.staffDetails?.contractData;
            if (contract) {
                if (contract.endDate) {
                    const end = new Date(contract.endDate);
                    const diffTime = end - new Date();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    daysRemaining = diffDays;
                } else if (contract.startDate && (contract.contractDuration || contract.contractMonths)) {
                    // Fallback calculation
                    const start = new Date(contract.startDate);
                    const duration = parseInt(contract.contractDuration || contract.contractMonths);
                    const unit = contract.contractUnit || 'months';
                    const end = new Date(start);

                    if (unit === 'days') {
                        end.setDate(end.getDate() + duration);
                    } else {
                        end.setMonth(end.getMonth() + duration);
                    }

                    const diffTime = end - new Date();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    daysRemaining = diffDays;
                }
            }

            // Payment Stats
            const userPayments = payments.filter(p => p.userId === user.discordId && p.status === 'approved');
            const approvedPayments = userPayments.length;
            const totalPaid = userPayments.reduce((sum, p) => sum + (parseFloat(p.totalValue) || 0), 0);

            return {
                ...user,
                stats: {
                    resolvedTickets,
                    pendingTickets,
                    approvedPayments,
                    totalPaid,
                    contractMonths: user.staffDetails?.contractData?.contractMonths || 0,
                    daysRemaining
                }
            };
        });
    }, [allUsers, tickets, payments]);

    const handleUpdateProvider = async (discordId, updatedDetails) => {
        const result = await adminFullUpdateUser(discordId, updatedDetails);
        if (result.success) {
            toast.success('Dados do prestador atualizados com sucesso!');
            setSelectedProvider(prev => ({ ...prev, ...updatedDetails, staffDetails: updatedDetails.staffDetails }));

            // Sync contract data with PaymentContext
            if (updatedDetails.staffDetails?.contractData) {
                updateContract(discordId, updatedDetails.staffDetails.contractData);
            }
        } else {
            toast.error('Erro ao atualizar prestador: ' + result.error);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await refreshAllUsers();
            setLastUpdated(new Date().toLocaleTimeString('pt-BR'));
            toast.success('Dados atualizados!');
        } catch (error) {
            toast.error('Erro ao atualizar dados');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleSuspendProvider = async (discordId) => {
        const result = await adminFullUpdateUser(discordId, {
            accessLevel: 'user',
            staffDetails: {
                ...selectedProvider.staffDetails,
                suspendedAt: new Date().toISOString(),
                isSuspended: true
            }
        });

        if (result.success) {
            toast.success('Prestador suspenso com sucesso!');
            setSelectedProvider(null);
        } else {
            toast.error('Erro ao suspender prestador: ' + result.error);
        }
    };

    const handleReactivateProvider = async (discordId) => {
        const roleToRestore = selectedProvider.staffDetails?.preStaffData?.accessLevel || 'suporte';

        const result = await adminFullUpdateUser(discordId, {
            accessLevel: roleToRestore,
            staffDetails: {
                ...selectedProvider.staffDetails,
                isSuspended: false,
                suspendedAt: null
            }
        });

        if (result.success) {
            toast.success('Acesso do prestador reativado!');
            setSelectedProvider(null);
        } else {
            toast.error('Erro ao reativar: ' + result.error);
        }
    };

    const handleDeleteProvider = async (discordId) => {
        const result = await deleteUser(discordId);
        if (result.success) {
            toast.success('Prestador excluído permanentemente.');
            setSelectedProvider(null);
        } else {
            toast.error('Erro ao excluir: ' + result.error);
        }
    };

    const handleImpersonate = async (discordId) => {
        const result = await startViewAsUser(discordId);
        if (result.success) {
            toast.success(result.message || "Simulando acesso...");
            if (result.isPreRegistered) {
                navigate('/staff-register');
            } else {
                navigate('/dashboard');
            }
        } else {
            toast.error(result.error || "Erro ao simular acesso");
        }
    };

    // Data Processing for Invites and Approvals
    const preStaffList = useMemo(() => {
        return Object.values(preRegisteredStaff).map(pre => {
            // Find if user is already in the system
            const existingUser = allUsers.find(u => u.discordId === pre.discordId);
            return {
                ...pre,
                existingUser
            };
        });
    }, [preRegisteredStaff, allUsers]);

    const pendingApprovals = useMemo(() => {
        return Object.values(staffRegistrations);
    }, [staffRegistrations]);

    // Actions
    const handleApprove = async (discordId) => {
        try {
            const result = await approveStaffRegistration(discordId);
            if (result.success) {
                toast.success('Cadastro aprovado com sucesso!');
                handleRefresh(); // Refresh all users to show the new staff
            } else {
                toast.error(result.error || 'Erro ao aprovar cadastro');
            }
        } catch (error) {
            toast.error('Erro ao processar aprovação');
        }
    };

    const handleRevokeInvite = async (discordId) => {
        try {
            const result = await removeStaffPreRegistration(discordId);
            if (result.success) {
                toast.success('Convite revogado com sucesso!');
            } else {
                toast.error(result.error || 'Erro ao revogar convite');
            }
        } catch (error) {
            toast.error('Erro ao processar revogação');
        }
    };

    return (
        <div className="p-8 space-y-8 min-h-screen bg-gray-950/50 pb-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <Briefcase className="h-8 w-8 text-purple-500" />
                        Gestão de Prestadores
                    </h1>
                    <p className="text-gray-400 mt-2">
                        Gerencie contratos, dados cadastrais e acessos.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Última atualização</p>
                        <p className="text-xs text-gray-400 font-mono">{lastUpdated}</p>
                    </div>
                    <Button
                        variant="outline"
                        size="icon"
                        className={`bg-gray-900/50 border-gray-800 hover:bg-gray-800 transition-all ${isRefreshing ? 'animate-spin' : ''}`}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </Button>

                    <Button
                        className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                        onClick={() => setCreateOpen(true)}
                    >
                        <UserPlus className="h-4 w-4" />
                        Novo Contrato
                    </Button>

                    <div className="bg-gray-900/50 px-4 py-2 rounded-lg border border-gray-800 flex items-center gap-3">
                        <Users className="h-5 w-5 text-gray-400" />
                        <span className="text-xl font-bold text-white">{providers.length}</span>
                        <span className="text-gray-500 text-sm font-medium uppercase tracking-wider">Registros</span>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="active" className="w-full">
                <TabsList className="grid w-full md:w-[600px] grid-cols-3 bg-gray-900/50 border border-white/10 p-1 rounded-xl mb-8">
                    <TabsTrigger
                        value="active"
                        className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 data-[state=active]:border-purple-500/20 border border-transparent rounded-lg transition-all"
                    >
                        <Briefcase className="h-4 w-4 mr-2" />
                        Ativos ({providers.length})
                    </TabsTrigger>
                    <TabsTrigger
                        value="pending"
                        className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400 data-[state=active]:border-yellow-500/20 border border-transparent rounded-lg transition-all"
                    >
                        <UserCheck className="h-4 w-4 mr-2" />
                        Validação ({pendingApprovals.length})
                    </TabsTrigger>
                    <TabsTrigger
                        value="invites"
                        className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 data-[state=active]:border-blue-500/20 border border-transparent rounded-lg transition-all"
                    >
                        <Clock className="h-4 w-4 mr-2" />
                        Convites ({preStaffList.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="active" className="space-y-4">
                    <ProviderList
                        providers={providers}
                        onSelectProvider={setSelectedProvider}
                        onRenew={openRenewalDialog}
                    />
                </TabsContent>

                <TabsContent value="pending">
                    <ApprovalsList
                        approvals={pendingApprovals}
                        onApprove={handleApprove}
                    />
                </TabsContent>

                <TabsContent value="invites">
                    <InvitesList
                        invites={preStaffList}
                        onRevoke={handleRevokeInvite}
                        onImpersonate={handleImpersonate}
                        onEdit={(staff) => {
                            // Opens Renewal Dialog pre-filled as "Edit"
                            // Or we could reuse ProviderCreateDialog in 'edit' mode if we had one.
                            // For now, let's use the Renewal Dialog logic which serves as an edit for existing/pre-data
                            setRenewalUser(staff);
                            setRenewalData({
                                value: staff.contractValue || '',
                                duration: staff.contractDuration || '',
                                unit: staff.contractUnit || 'months',
                                role: staff.accessLevel,
                                description: staff.serviceDescription || ''
                            });
                            setRenewalOpen(true);
                        }}
                    />
                </TabsContent>
            </Tabs>

            <ProviderCreateDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onSuccess={() => {
                    handleRefresh();
                    toast.success("Contrato criado com sucesso");
                }}
            />

            {/* Slide-over Details */}
            {selectedProvider && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 transition-opacity"
                        onClick={() => setSelectedProvider(null)}
                    />
                    <ProviderDetails
                        provider={selectedProvider}
                        userPayments={payments.filter(p => p.userId === selectedProvider.discordId)}
                        onClose={() => setSelectedProvider(null)}
                        onUpdate={handleUpdateProvider}
                        onSuspend={handleSuspendProvider}
                        onReactivate={handleReactivateProvider}
                        onDelete={handleDeleteProvider}
                        onImpersonate={handleImpersonate}
                    />
                </>
            )}

            <Dialog open={renewalOpen} onOpenChange={setRenewalOpen}>
                <DialogContent className="bg-[#09090b] border border-gray-800 text-white sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle>
                            {providers.find(p => p.discordId === renewalUser?.discordId) ? 'Renovar Contrato' : 'Editar Contrato'}
                            : {renewalUser?.name || renewalUser?.codename}
                        </DialogTitle>
                        <DialogDescription>
                            {providers.find(p => p.discordId === renewalUser?.discordId)
                                ? 'Defina os termos para a renovação do contrato.'
                                : 'Ajuste os termos do convite.'
                            }
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-400">Novo Valor</label>
                                <Input
                                    value={renewalData.value}
                                    onChange={(e) => setRenewalData({ ...renewalData, value: e.target.value })}
                                    className="bg-gray-900 border-gray-800"
                                    placeholder="0000"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-400">Nova Duração</label>
                                <div className="flex gap-2">
                                    <Input
                                        value={renewalData.duration}
                                        onChange={(e) => setRenewalData({ ...renewalData, duration: e.target.value })}
                                        className="bg-gray-900 border-gray-800"
                                        placeholder="12"
                                    />
                                    <Select
                                        value={renewalData.unit}
                                        onValueChange={(val) => setRenewalData({ ...renewalData, unit: val })}
                                    >
                                        <SelectTrigger className="w-[110px] bg-gray-900 border-gray-800 text-white">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-gray-900 border-gray-800 text-white">
                                            <SelectItem value="months">Meses</SelectItem>
                                            <SelectItem value="days">Dias</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400">Novo Cargo</label>
                            <Select
                                value={renewalData.role}
                                onValueChange={(val) => setRenewalData({ ...renewalData, role: val })}
                            >
                                <SelectTrigger className="bg-gray-900 border-gray-800 text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-gray-900 border-gray-800 text-white">
                                    <SelectItem value="suporte">Suporte</SelectItem>
                                    <SelectItem value="suporte_compras">Suporte Compras</SelectItem>
                                    <SelectItem value="suporte_descontos">Suporte Descontos</SelectItem>
                                    <SelectItem value="moderador">Moderador</SelectItem>
                                    <SelectItem value="super_moderador">Super Moderador</SelectItem>
                                    <SelectItem value="programador">Programador</SelectItem>
                                    <SelectItem value="head_suporte">Head Suporte</SelectItem>
                                    <SelectItem value="head_moderacao">Head Moderação</SelectItem>
                                    <SelectItem value="head_staff">Head Staff</SelectItem>
                                    <SelectItem value="analista_qualidade">Analista de Qualidade</SelectItem>
                                    <SelectItem value="departamento_juridico">Departamento Jurídico</SelectItem>
                                    <SelectItem value="criador_de_conteudo">Criador de Conteúdo</SelectItem>
                                    <SelectItem value="admin">Administrador</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400">Descrição do Serviço</label>
                            <textarea
                                value={renewalData.description}
                                onChange={(e) => setRenewalData({ ...renewalData, description: e.target.value })}
                                className="w-full h-24 bg-gray-900 border border-gray-800 rounded-md p-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenewalOpen(false)} className="border-gray-700 text-gray-300 hover:bg-gray-800">
                            Cancelar
                        </Button>
                        <Button onClick={handleRenewalSubmit} className="bg-purple-600 hover:bg-purple-700 text-white">
                            {providers.find(p => p.discordId === renewalUser?.discordId) ? 'Enviar Proposta' : 'Salvar Alterações'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminProviders;
